/**
 * Feature Flag Manager Window
 *
 * Visual UI for managing feature flags across all domains. Allows toggling
 * features, managing whitelist mode, and viewing current state. Access via
 * DevTools or add to main menu.
 *
 * Orchestrator only — chrome is composed from theme tokens + UI primitives,
 * and each region (sidebar / toolbar / feature grid / stats / help) lives in
 * its own co-located sub-component. Behavior is preserved 1:1 from the prior
 * monolith: same hooks, same handlers, same feature-toggle semantics.
 */

import React, { useState, useMemo } from 'react';
import {
  featureFlags,
  FeatureMode,
  useFeatureDomains,
  useFeatureMode,
  useFeatureWhitelist,
  useFeatureActions,
} from '../../../../core/features';
import type { FeatureConfig } from '../../../../core/features';
import { FeatureDomainSidebar } from './FeatureDomainSidebar';
import { FeatureToolbar } from './FeatureToolbar';
import { FeatureGrid, type GroupedFeature } from './FeatureGrid';
import { FeatureStatsBar } from './FeatureStatsBar';
import { FeatureHelpPanel } from './FeatureHelpPanel';
import {
  containerStyle,
  headerStyle,
  titleStyle,
  subtitleStyle,
  layoutStyle,
  contentStyle,
  emptyStateStyle,
} from './featureManagerStyles';
import { Button } from '../../primitives';

interface FeatureManagerWindowProps {
  onClose: () => void;
}

export const FeatureManagerWindow: React.FC<FeatureManagerWindowProps> = ({ onClose }) => {
  const domains = useFeatureDomains();
  const [selectedDomain, setSelectedDomain] = useState<string>(domains[0] || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const { setMode, toggle, whitelist, unwhitelist, clearWhitelist, setWhitelist } = useFeatureActions();

  // Get domain config
  const domainConfig = useMemo(() => {
    return featureFlags.getDomainConfig(selectedDomain);
  }, [selectedDomain]);

  const currentMode = useFeatureMode(selectedDomain);
  const whitelistState = useFeatureWhitelist(selectedDomain);

  // Group features by category
  const groupedFeatures = useMemo(() => {
    if (!domainConfig) return {};

    const groups: Record<string, GroupedFeature[]> = {};

    Object.entries(domainConfig.features).forEach(([name, config]) => {
      const featureConfig = config as FeatureConfig;
      const category = featureConfig.category || 'uncategorized';
      if (!groups[category]) groups[category] = [];
      groups[category].push({ name, config: featureConfig });
    });

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      Object.keys(groups).forEach((category) => {
        groups[category] = groups[category].filter(
          ({ name, config }) =>
            name.toLowerCase().includes(query) ||
            (config.description && config.description.toLowerCase().includes(query)),
        );
        if (groups[category].length === 0) delete groups[category];
      });
    }

    return groups;
  }, [domainConfig, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    if (!domainConfig) return { total: 0, enabled: 0, whitelisted: 0 };
    const total = Object.keys(domainConfig.features).length;
    const enabled = Object.values(domainConfig.features).filter((f) => f.enabled).length;
    return { total, enabled, whitelisted: whitelistState.length };
  }, [domainConfig, whitelistState]);

  // Handlers
  const handleToggle = (feature: string) => {
    toggle(selectedDomain, feature);
  };

  const handleModeChange = (mode: FeatureMode) => {
    setMode(selectedDomain, mode);
  };

  const handleWhitelistToggle = (feature: string) => {
    if (whitelistState.includes(feature)) {
      unwhitelist(selectedDomain, feature);
    } else {
      whitelist(selectedDomain, feature);
    }
  };

  const handleWhitelistOnly = (feature: string) => {
    setWhitelist(selectedDomain, [feature]);
    setMode(selectedDomain, FeatureMode.WHITELIST);
  };

  if (!selectedDomain) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            No Feature Domains Found
          </div>
          <div>Initialize the FeatureFlagService first.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <div style={titleStyle}>
            <span>🚦</span>
            Feature Flag Manager
          </div>
          <div style={subtitleStyle}>Manage features across all domains</div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          ×
        </Button>
      </div>

      {/* Main Layout */}
      <div style={layoutStyle}>
        <FeatureDomainSidebar
          domains={domains}
          selectedDomain={selectedDomain}
          onSelect={setSelectedDomain}
        />

        {/* Content */}
        <div style={contentStyle}>
          <FeatureToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            currentMode={currentMode}
            onModeChange={handleModeChange}
            onToggleHelp={() => setShowHelp(!showHelp)}
            onClearWhitelist={() => clearWhitelist(selectedDomain)}
          />

          <FeatureGrid
            domain={selectedDomain}
            groupedFeatures={groupedFeatures}
            currentMode={currentMode}
            whitelistState={whitelistState}
            searchQuery={searchQuery}
            onToggle={handleToggle}
            onWhitelistToggle={handleWhitelistToggle}
            onSolo={handleWhitelistOnly}
          />

          <FeatureStatsBar
            total={stats.total}
            enabled={stats.enabled}
            whitelisted={stats.whitelisted}
            currentMode={currentMode}
          />
        </div>
      </div>

      {/* Help Panel */}
      {showHelp && <FeatureHelpPanel />}
    </div>
  );
};
